import { ISubmission, IAssignment } from '@/components/interfaces';
import cn from '@/utils/cn';
import { IAssignmentDetails } from '@/components/interfaces/Assignment';

export const TABLE_ROW_CLASS = 'tw-text-start tw-w-[14%]';
export const TABLE_HEADER_CLASS = cn(TABLE_ROW_CLASS, 'tw-pb-4');
export const TABLE_ROW_WITH_BORDER_CLASS = cn(TABLE_ROW_CLASS, 'tw-p-4 tw-border-t tw-border-med-grey tw-leading-5',
  'tw-border-solid tw-align-middle');

export type StudentRowDataType = {
  assignment_details: Partial<IAssignmentDetails>,
  assignment_id: number,
  due_datetime: string,
  graded_at?: string,
  investigation?: boolean,
  in_progress?: string,
  max_points: number,
  status: string,
  submission_id: number | null,
  submitted_at?: string | null,
  total_score?: number | null,
  total_score_override?: number | null,
};

// Returns the value at the end of the path if it exists
// Helper: safely get nested value from object by dot-path
export const getValueByPath = (
  obj: any,
  path: string,
) => path.split('.').reduce((acc, key) => acc?.[key], obj);

// Helper: status sorting logic
const getStatusOrder = (row: StudentRowDataType) => {
  // 'to_do' but has no submission_id means not started
  if (row.status === 'to_do' && !row.submission_id) return 1;
  if (row.status === 'to_do') return 2;
  if (row.status === 'completed') return 3;
  return -1;
};

const compareValues = (a: any, b: any, order: 'asc' | 'desc' = 'asc') => {
  // Place null/undefined values at the end
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;

  if (a < b) return order === 'asc' ? -1 : 1;
  if (a > b) return order === 'asc' ? 1 : -1;
  return 0;
};

export const sortTableData = (
  path: string,
  data: StudentRowDataType[] = [],
  order: 'asc' | 'desc' = 'asc',
): StudentRowDataType[] => [...data].sort((a, b) => {
  if (path === 'status') {
    return compareValues(getStatusOrder(a), getStatusOrder(b), order);
  }

  if (path === 'total_score') {
    return compareValues(getValueByPath(a, 'total_score_override') ?? getValueByPath(a, path),
      getValueByPath(b, 'total_score_override') ?? getValueByPath(b, path), order);
  }

  const valueA = getValueByPath(a, path);
  const valueB = getValueByPath(b, path);
  return compareValues(valueA, valueB, order);
});

export const mergeTableData = (
  assignments : Partial<IAssignment>[],
  submissions : Partial<ISubmission>[],
) => assignments.map((assignment) => {
  const submission = submissions.find(sub => sub.assignment_id === assignment.id);

  return {
    assignment_details: { ...assignment.assignment_details },
    assignment_id: assignment.id,
    due_datetime: assignment.due_datetime,
    graded_at: submission?.graded_at,
    investigation: assignment.investigation,
    max_points: assignment.max_points,
    status: assignment.status || 'not_submitted',
    submission_id: submission?.id ?? null,
    submitted_at: submission?.submitted_at ?? null,
    total_score: submission?.total_score ?? null,
    total_score_override: submission?.total_score_override ?? null,
  };
});

export const getSubmissionScore = (rowData: StudentRowDataType) => {
  if (rowData.assignment_details.type === 'Assessment') {
    if (!rowData.graded_at) return null;
  }

  return rowData.total_score_override ?? rowData.total_score;
};

export const isAssignmentAutoGradeableOnly = (assignment: Partial<IAssignment>) => {
  const entries = Object.entries(assignment?.grouped_questions ?? {});
  // An assignment with no questions (e.g. reading-only) is not auto-gradeable only —
  // Array.every() on an empty array returns true vacuously, which would incorrectly
  // disable score editing for reading-only assignments that teachers must grade manually.
  if (entries.length === 0) return false;
  return entries.every(([, groupedQuestions]) => groupedQuestions.every((q: any) => q.auto_gradable));
};
